import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Moon, Sun, Plus, Menu, User, Mail, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
} from "@/components/ui/sheet";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { APP_CONFIG } from "@/lib/config";
import { cn } from "@/lib/utils";

export function TopBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { isAuthenticated, userInfo, login, logout } = useAuth();
  const displayName = userInfo?.nickname || userInfo?.username || "";
  const avatarFallback = displayName.charAt(0) || "U";

  // ⌘K / Ctrl+K 聚焦搜索
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setMobileOpen(false);
  };

  const toggleTheme = () => setTheme(resolvedTheme === "dark" ? "light" : "dark");
  const handleLogout = () => logout();

  return (
    <header className="sticky top-0 z-40 h-14 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6">
        {/* Logo + 品牌名 */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src="/logo.svg" alt="logo" className="h-7 w-7" />
          <span className="hidden text-[15px] font-semibold tracking-tight sm:inline">
            {APP_CONFIG.appName}
          </span>
        </Link>

        <div className="hidden h-5 w-px bg-border md:block" />

        {/* 桌面端导航 */}
        <nav className="hidden items-center gap-0.5 md:flex">
          <NavLink to="/discover" active={pathname === "/discover"}>
            发现
          </NavLink>

        </nav>

        {/* 搜索框 — 命令面板风格 */}
        <form onSubmit={handleSearch} className="ml-auto hidden lg:block">
          <div className="group relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-foreground" />
            <input
              ref={searchInputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索提示词..."
              className="h-9 w-56 rounded-lg border border-transparent bg-muted/60 pl-9 pr-12 text-sm text-foreground transition-all placeholder:text-muted-foreground focus:w-64 focus:border-border focus:bg-background focus:outline-none"
            />
            <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 select-none rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </div>
        </form>

        {/* 右侧操作区 */}
        <div className="flex items-center gap-1.5 lg:ml-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label="切换主题"
            className="h-9 w-9 text-foreground/60"
          >
            {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-0.5 flex items-center gap-2 rounded-full ring-offset-background transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <Avatar className="h-8 w-8" src={userInfo?.avatar} fallback={avatarFallback} />
                  <span className="hidden max-w-[120px] truncate text-sm font-medium sm:inline">
                    {displayName}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* 用户信息摘要 */}
                <div className="flex items-center gap-3 px-2 py-1.5">
                  <Avatar className="h-10 w-10" src={userInfo?.avatar} fallback={avatarFallback} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{displayName}</p>
                    {userInfo?.email && (
                      <p className="truncate text-xs text-muted-foreground">{userInfo.email}</p>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my-submissions")}>
                  <User className="mr-2 h-4 w-4" />
                  我的提交
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")}>
                  <Mail className="mr-2 h-4 w-4" />
                  我的收藏
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/submit")}>
                  <Plus className="mr-2 h-4 w-4" />
                  提交提示词
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={login} className="h-9">
              登录
            </Button>
          )}

          {/* 移动端汉堡菜单 */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="打开菜单"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* 移动端抽屉菜单 */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetOverlay onClick={() => setMobileOpen(false)} />
        <SheetContent side="left" onClose={() => setMobileOpen(false)} className="w-72">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <img src="/logo.svg" alt="logo" className="h-5 w-5" />
              {APP_CONFIG.appName}
            </SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索提示词..."
                  className="pl-9"
                />
              </div>
            </form>
            <nav className="flex flex-col gap-1">
              <MobileLink to="/discover" active={pathname === "/discover"} onClose={() => setMobileOpen(false)}>
                发现
              </MobileLink>
            </nav>
            {/* 移动端用户信息 */}
            {isAuthenticated && userInfo && (
              <div className="flex items-center gap-3 border-b pb-4">
                <Avatar className="h-10 w-10" src={userInfo.avatar} fallback={avatarFallback} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{displayName}</p>
                  {userInfo.email && (
                    <p className="truncate text-xs text-muted-foreground">{userInfo.email}</p>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2 border-t pt-4">
              {isAuthenticated && (
                <>
                  <MobileLink to="/submit" onClose={() => setMobileOpen(false)}>
                    <Plus className="h-4 w-4" />
                    提交提示词
                  </MobileLink>
                  <MobileLink to="/my-submissions" onClose={() => setMobileOpen(false)}>
                    我的提交
                  </MobileLink>
                  <MobileLink to="/favorites" onClose={() => setMobileOpen(false)}>
                    我的收藏
                  </MobileLink>
                  <button
                    onClick={() => { handleLogout(); setMobileOpen(false); }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-accent hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                    退出登录
                  </button>
                </>
              )}
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function NavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-accent text-foreground"
          : "text-foreground/60 hover:bg-accent hover:text-foreground",
      )}
    >
      {children}
    </Link>
  );
}

function MobileLink({
  to,
  children,
  onClose,
  active,
  className,
}: {
  to: string;
  children: React.ReactNode;
  onClose: () => void;
  active?: boolean;
  className?: string;
}) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className={cn(
        "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-accent text-foreground"
          : "text-foreground/70 hover:bg-accent hover:text-foreground",
        className,
      )}
    >
      {children}
    </Link>
  );
}
